import React, { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Bell, Users, Users2, FileText, CalendarDays, Printer,
         Database, CheckCircle2, Hourglass, GraduationCap, BarChart3, TrendingUp, PieChart,
         Coins, Star, Smartphone, Table2 } from "lucide-react";
import { api, argent } from "./api";
import { Carte, Bloc, TONS_BLOC, Tableau, Cellule, Barre, Eyebrow, CarteIcone, CarteIconeSombre, Panneau } from "./ui";
import { Graphiques, ParCours } from "./Graphiques";
import { CartesApplication } from "./Application";
import { SectionAssistant, SectionProfs, SectionPrives, SectionAttestations } from "./DashboardPlus";
import { SectionSite } from "./DashboardSite";
import SectionEssais from "./EssaisTableau";
import { useChantiers } from "../lib/useChantiers";

/* Le sommaire du haut : une pastille par section, à sa couleur, qui y mène d'un clic. */
const SOMMAIRE = [
  ["tdb-eleves", "Élèves", "eleves"], ["tdb-argent", "Argent", "argent"], ["tdb-cours", "Cours", "cours"],
  ["tdb-essais", "Cours d'essai", "essais"], ["tdb-annexes", "Stages et spectacles", "annexes"],
  ["tdb-assistant", "Assistant IA", "assistant"],
  ["tdb-profs", "Profs", "profs"], ["tdb-attestations", "Attestations", "attestations"],
  ["tdb-application", "Application", "application"], ["tdb-site", "Le site web", "site"], ["tdb-analyses", "Graphiques et tableaux", "analyses"],
];

const allerA = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

export default function Dashboard({ nbActions, onVoirNotifications, onOuvrirCours, onVoirEleves, onVoirListe, onOuvrir, cible }) {
  const [s, setS] = useState(null);
  // « Bonjour Miller 👋 » : le même accueil personnel que dans les applications.
  const [prenom, setPrenom] = useState("");
  useEffect(() => { api("/moi").then((m) => setPrenom(String(m?.nom || "").split(" ")[0])).catch(() => {}); }, []);
  const [err, setErr] = useState("");
  /* D'où vient l'argent encaissé : compte de l'école, compte de Noémie, espèces. */
  const [repartition, setRepartition] = useState(null);
  const jourFr = (iso) => String(iso || "").slice(0, 10).split("-").reverse().join(".");

  const charger = useCallback(() => {
    api("/stats").then(setS).catch((e) => setErr(e.message));
    api("/encaissements/repartition").then(setRepartition).catch(() => {});
  }, []);
  useEffect(charger, [charger]);
  // Un lien vers une section (« ?onglet=essais », l'ancien écran Élèves › Cours d'essai) :
  // le tableau de bord s'ouvre sur elle une fois les chiffres chargés.
  useEffect(() => {
    if (!s || !String(cible || "").startsWith("tdb-")) return undefined;
    const t = setTimeout(() => allerA(cible), 80);
    return () => clearTimeout(t);
  }, [s, cible]);

  if (err) return <p className="text-sm" style={{ color: "#B91C1C" }}>{err === "session" ? "Session expirée, reconnecte-toi." : err}</p>;
  if (!s) return <p className="text-[var(--muted)]">Chargement…</p>;

  const a = s.argent;
  const sc = s.semestre_courant === 2 ? a.s2 : a.s1;
  const sv = s.semestre_courant === 2 ? a.s1 : a.s2;
  /* Le reste à encaisser, morceau par morceau, avec les noms des filtres de Facturation :
     « 3 773 CHF pas encore facturés » à côté de « 0 facture à envoyer » faisait croire à
     des factures oubliées, alors que c'était surtout du cash et des factures à corriger
     (20.10.2026). Les morceaux font le total de la carte. */
  const morceau = (k) => (sc.hors_envoi?.[k] || 0) + (a.stages.hors_envoi?.[k] || 0);
  const resteParMorceau = sc.hors_envoi && a.stages.hors_envoi ? [
    [`En attente de paiement (${sc.impayees + a.stages.impayees} factures)`,
      sc.facture_impaye + a.stages.facture_impaye, "envoyees"],
    ["Cash / espèces, pas encore reçu", morceau("especes"), "cash"],
    ["À corriger, pas encore envoyées", morceau("a_corriger"), "a-corriger"],
    ["À envoyer", morceau("a_envoyer"), "a-envoyer"],
    ["Non facturées", morceau("non_facturees"), "non-facturees"],
    ["Trop-perçus, déduits", -morceau("trop_percus"), "trop-percus"],
  ].filter(([, v]) => Math.abs(v) >= 0.5) : null;
  return (
    <div className="space-y-6" data-testid="console-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Eyebrow>Tableau de bord · {s.saison ? `saison ${s.saison}` : "saison en cours"}</Eyebrow>
          <h1 className="font-display text-3xl tracking-tight mt-1" data-testid="console-salut">
            {new Date().getHours() < 5 || new Date().getHours() >= 18 ? "Bonsoir" : "Bonjour"}{prenom ? ` ${prenom}` : ""} 👋
          </h1>
        </div>
      </div>

      <nav aria-label="Sommaire du tableau de bord" className="flex flex-wrap gap-2" data-testid="dashboard-sommaire">
        {SOMMAIRE.map(([id, label, ton]) => (
          <a key={id} href={`#${id}`} data-testid={`sommaire-${id}`}
            onClick={(e) => { e.preventDefault(); allerA(id); }}
            className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1.5 border bg-white transition-transform hover:-translate-y-0.5"
            style={{ borderColor: TONS_BLOC[ton].bord, color: TONS_BLOC[ton].texte }}>
            <span className="w-2 h-2 rounded-full" style={{ background: TONS_BLOC[ton].accent }} />
            {label}
          </a>
        ))}
      </nav>

      {nbActions > 0 && (
        <button
          onClick={onVoirNotifications}
          data-testid="dashboard-bandeau-actions"
          className="w-full text-left rounded-2xl px-5 py-4 flex items-center gap-4 transition-transform hover:-translate-y-0.5"
          style={{ background: "#FFFBEB", border: "1px solid #FDE68A" }}
        >
          <span className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "#F59E0B", color: "#fff" }}>
            <Bell size={17} />
          </span>
          <span>
            <span className="font-semibold" style={{ color: "#B45309" }}>{nbActions} action(s) en attente</span>
            <span className="block text-xs" style={{ color: "#92400E" }}>Doublons, factures, relances, numéros AVS, messages — voir la liste complète</span>
          </span>
          <span className="ml-auto text-sm font-semibold" style={{ color: "#B45309" }}>Ouvrir →</span>
        </button>
      )}

      {s.feuilles_a_refaire?.nb_cours > 0 && (
        <button
          onClick={onVoirListe}
          data-testid="dashboard-bandeau-nouveaux"
          className="w-full text-left rounded-2xl px-5 py-4 flex items-center gap-4 transition-transform hover:-translate-y-0.5"
          style={{ background: "#ECFEFF", border: "1px solid #A5F3FC" }}
        >
          <span className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "#0891B2", color: "#fff" }}>
            <Printer size={17} />
          </span>
          <span>
            <span className="font-semibold" style={{ color: "#0E7490" }}>
              {s.feuilles_a_refaire.nb_cours} cours accueillent de nouveaux élèves
            </span>
            <span className="block text-xs" style={{ color: "#155E75" }}>
              {s.feuilles_a_refaire.nb_personnes} inscription(s) et essai(s) arrivés depuis la dernière impression
              {s.feuilles_a_refaire.jamais_imprimes > 0
                ? ` · ${s.feuilles_a_refaire.jamais_imprimes} cours jamais imprimé(s)`
                : ""} — feuilles de présence à ressortir
            </span>
          </span>
          <span className="ml-auto text-sm font-semibold" style={{ color: "#0E7490" }}>Ouvrir la liste →</span>
        </button>
      )}

      <Bloc id="tdb-eleves" Icon={Users} ton="eleves" titre="Les élèves"
        aide="Élèves actifs aujourd'hui : une annulation ou une désinscription ne sort du compte qu'après le dernier cours suivi."
        testid="section-eleves">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <CarteIcone titre="Élèves inscrits" valeur={s.eleves} detail="personnes différentes"
            Icon={Users} teinte="bleu" onClick={onVoirEleves} testid="stat-eleves" />
          {/* Ce qui est compté : les fiches famille qui ont au moins un élève inscrit — pas
              les adresses e-mail (deux fiches peuvent partager la même adresse). */}
          <CarteIcone titre="Familles" valeur={s.familles} detail="fiches famille avec au moins un élève inscrit"
            Icon={Users2} teinte="rose" testid="stat-familles" />
          <CarteIcone titre="Inscriptions à des cours" valeur={s.inscriptions_actives} detail={`${s.inscriptions} lignes au total`}
            Icon={FileText} teinte="vert" onClick={onVoirListe} testid="stat-inscriptions" />
          <CarteIcone titre="Réservations d'essai" valeur={s.essais} detail={`${s.essais_a_venir} à venir · ${s.essais_passes} passées · voir la section →`}
            Icon={CalendarDays} teinte="violet" onClick={() => allerA("tdb-essais")} testid="stat-essais" />
        </div>
      </Bloc>

      <Bloc id="tdb-argent" Icon={Coins} ton="argent" titre={`L'argent — semestre ${s.semestre_courant} en cours`}
        aide="Le semestre en cours est le seul dont les montants sont sûrs. Le reste est une prévision."
        testid="section-argent">
        <Panneau titre="Performance du semestre en cours" sombre testid="panneau-semestre">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <CarteIconeSombre titre="Total à facturer — cours et stages" valeur={argent(sc.du + a.stages.du)} Icon={Database} pastille="#60A5FA"
              detail={`${argent(sc.du)} de cours · ${argent(a.stages.du)} de stages · ${sc.a_envoyer} facture(s) à envoyer`} testid="stat-semestre" />
            {/* Cours ET stages, comme le total juste à côté : annoncer les cours seuls ici
                ne collait pas avec l'écran Facturation (demandé le 02.10.2026). */}
            <CarteIconeSombre titre="Déjà encaissé" valeur={argent(sc.encaisse + a.stages.encaisse)} accent="#86EFAC" Icon={CheckCircle2} pastille="#34D399"
              detail={s.releve?.date_releve
                ? `paiements connus jusqu'au ${jourFr(s.releve.date_releve)} (dernier relevé bancaire)`
                : s.releve?.present
                  ? "relevé bancaire sans date lisible — importe le dernier relevé"
                  : "aucun relevé bancaire importé : les paiements ne sont pas pointés"}
              testid="stat-encaisse"
              /* D'où vient l'argent : chaque compte, puis les espèces. Discret, sous le
                 montant, pour ne pas charger la carte (demandé le 02.10.2026). */
              enfants={repartition ? (
                <div className="mt-2 space-y-0.5 text-[11px]" data-testid="stat-encaisse-split"
                  style={{ color: "rgba(255,255,255,0.72)" }}>
                  {repartition.comptes.filter((c) => c.montant > 0).map((c) => (
                    <div key={c.cle}>{c.libelle} · <strong>{argent(c.montant)}</strong></div>
                  ))}
                  {repartition.especes > 0 && (
                    <div title="Tout ce qu'aucun relevé bancaire ne couvre : les relevés de Miller et de Noémie font foi pour les virements.">
                      Espèces · <strong>{argent(repartition.especes)}</strong>
                    </div>
                  )}
                  {/* Sans cette ligne, les morceaux ne faisaient pas le total (recette du
                      08.10.2026 : 1 506 + 410 affichés pour 2 088 encaissés). */}
                  {repartition.non_rapproches > 0 && (
                    <div data-testid="stat-encaisse-a-rapprocher"
                      title="Virements ou TWINT notés à la main sur une facture, qu'aucun relevé bancaire importé ne couvre encore : ils rejoignent leur compte à l'import du relevé.">
                      {/* « À rapprocher » ne se comprenait pas (20.10.2026). */}
                      Virements pas encore vus sur un relevé · <strong>{argent(repartition.non_rapproches)}</strong>
                    </div>
                  )}
                </div>
              ) : null} />
            {/* Deux manques très différents : ce qui est facturé et pas payé, et ce qui
                n'est pas encore facturé. Les additionner sans le dire empêchait de
                retrouver le chiffre de Facturation. */}
            <CarteIconeSombre titre="Reste à encaisser" valeur={argent(sc.reste + a.stages.reste)} accent="#FCA5A5" Icon={Hourglass} pastille="#FCA5A5"
              detail={resteParMorceau ? "cours et stages, dont :"
                : `${argent(sc.facture_impaye + a.stages.facture_impaye)} facturés et impayés (${sc.impayees + a.stages.impayees} facture(s)) · ${argent(sc.pas_encore_facture + a.stages.reste - a.stages.facture_impaye)} pas encore facturés`}
              testid="stat-reste"
              enfants={resteParMorceau ? (
                <div className="mt-1 space-y-0.5 text-[11px]" data-testid="stat-reste-morceaux"
                  style={{ color: "rgba(255,255,255,0.72)" }}>
                  {resteParMorceau.map(([libelle, v, cle]) => (
                    <div key={cle} data-testid={`stat-reste-${cle}`}>
                      {libelle} · <strong>{v < 0 ? "− " : ""}{argent(Math.abs(v))}</strong>
                    </div>
                  ))}
                </div>
              ) : null} />
            <CarteIconeSombre titre="Stages" valeur={argent(a.stages.du)} accent="#FDE68A" Icon={GraduationCap} pastille="#FCD34D"
              detail={`${argent(a.stages.reste)} à encaisser · ${a.stages.a_envoyer} à envoyer`} testid="stat-stages" />
          </div>
        </Panneau>

        <Panneau titre="Prévisions et suivi annuel" testid="panneau-annuel">
          <div className="grid sm:grid-cols-3 gap-4">
            <CarteIcone titre={`Prévision semestre ${s.semestre_courant === 1 ? 2 : 1}`} valeur={argent(sv.du)}
              detail="dépendra des désinscriptions et des nouveaux élèves" Icon={BarChart3} teinte="ambre"
              testid="stat-prevision-semestre" />
            <CarteIcone titre="Prévision année complète" valeur={argent(a.annuel.du)}
              detail="semestre en cours + prévision du suivant" Icon={TrendingUp} teinte="neutre"
              testid="stat-prevision-annee" />
            <CarteIcone titre="Encaissé sur l'année" valeur={argent(a.annuel.encaisse)} Icon={PieChart} teinte="vert"
              detail={`${argent(a.annuel.reste)} restants sur la saison`} testid="stat-annuel"
              enfants={<div className="mt-2"><Barre pourcent={a.annuel.du ? Math.round(100 * a.annuel.encaisse / a.annuel.du) : 0} /></div>} />
          </div>
        </Panneau>
      </Bloc>

      <Bloc id="tdb-cours" Icon={CalendarDays} ton="cours" titre="Les cours" aide="Planning de la semaine, cours actifs uniquement." testid="section-cours">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Carte titre="Cours actifs" valeur={s.cours_actifs} detail={`${s.cours_total} au planning`} ton="primaire" testid="stat-cours" />
          <Carte titre="Places occupées" valeur={`${s.places_occupees} / ${s.places_offertes}`} detail="occupées sur places offertes" testid="stat-places" />
          <Carte titre="Taux de remplissage" valeur={`${s.remplissage} %`} detail="moyenne sur les cours actifs" ton={s.remplissage < 40 ? "ambre" : "vert"} testid="stat-remplissage" />
          <Carte titre="Si tous les cours étaient pleins" valeur={argent(s.potentiel.montant)}
            detail={`${s.potentiel.places} places au tarif moyen de ${argent(s.potentiel.tarif_moyen)} · ${argent(s.potentiel.manque)} de potentiel non vendu`}
            ton="primaire" testid="stat-potentiel" />
          <Carte titre="Cours sans aucun inscrit" valeur={s.cours_sans_inscrit.length} detail="à surveiller" ton={s.cours_sans_inscrit.length ? "ambre" : "neutre"} testid="stat-sans-inscrit" />
        </div>
        {/* Les mouvements (école, 30.10.2026) : qui arrive, qui part, les cartes 10 cours. */}
        {s.mouvements && s.mouvements.nouvelles_30j !== undefined && (
          <div className="grid sm:grid-cols-3 gap-4" data-testid="stat-mouvements">
            <Carte titre="Nouvelles inscriptions (30 derniers jours)" valeur={s.mouvements.nouvelles_30j}
              detail={`${s.mouvements.nouvelles_30j_eleves} élève(s) · dont ${s.mouvements.nouvelles_30j_cartes} carte(s) 10 cours`}
              ton="vert" testid="stat-nouvelles-30j" />
            <Carte titre={`Désinscriptions du semestre ${s.semestre_courant}`} valeur={s.mouvements.desinscriptions_semestre}
              detail="annoncées depuis le début du semestre" ton={s.mouvements.desinscriptions_semestre ? "ambre" : "neutre"}
              testid="stat-desinscriptions-semestre" />
            <Carte titre="Cartes 10 cours" valeur={s.mouvements.cartes_10}
              detail={`${s.mouvements.cartes_10_encaissees} encaissée(s) au comptoir · ${s.mouvements.cartes_10_sur_facture} sur facture · voir la liste →`}
              onClick={() => onOuvrir?.("cartes-10")} testid="stat-cartes-10" />
          </div>
        )}
        <div className="grid lg:grid-cols-2 gap-4">
          <ListeParJour titre="Cours à moins de 4 élèves" Icon={AlertTriangle} ton="ambre"
            items={s.cours_sous_effectif} onOuvrir={onOuvrirCours} testid="liste-sous-effectif"
            aide="Un cours sous-rempli ne couvre pas son professeur." />
          <ListeParJour titre="Cours complets" Icon={CheckCircle2} ton="vert"
            items={s.complets} onOuvrir={onOuvrirCours} testid="liste-complets"
            aide="Les inscriptions suivantes partent en liste d'attente." />
        </div>
      </Bloc>

      {/* Les cours d'essai, une section à eux (Jen, 01.11.2026 : « le menu Cours d'essai dans
          Élèves doit être déplacé dans le tableau de bord ») : chiffres, par mois, par cours,
          avis des familles. Ses chiffres ne sont plus dans « Stages, auditions et spectacles ». */}
      <SectionEssais />

      <Bloc id="tdb-annexes" Icon={Star} ton="annexes" titre="Stages, auditions et spectacles" testid="section-annexes"
        aide="Les inscrits aux auditions et aux stages, les places du spectacle de Noël et les billets des spectacles de juin.">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Carte titre="Inscrits à l'audition" valeur={s.auditions} detail="toutes auditions confondues" testid="stat-auditions" />
          <Carte titre="Inscrits aux stages" valeur={s.stages} detail="toutes sessions confondues" testid="stat-stages-inscrits" />
        </div>
        <ChiffresSpectacles s={s} onOuvrir={onOuvrir} />
      </Bloc>

      {/* Assistant IA, Profs, Attestations (école, 12.10.2026) : entre les essais et
          l'application des familles. Chacune charge ses chiffres après ceux du haut. */}
      <SectionAssistant onOuvrir={onOuvrir} />
      <SectionProfs onOuvrir={onOuvrir} />
      <SectionPrives onOuvrir={onOuvrir} />
      <SectionAttestations onOuvrir={onOuvrir} />

      {/* Les chiffres de l'application des familles, venus de l'ancien menu Application
          (école, 09.10.2026). Livrés par /stats : aucune demande de plus au chargement.
          Une section à part entière, avec le même grand titre que les autres : en petit
          libellé dans un encadré, sous les essais, on ne la voyait pas (école, 09.10.2026). */}
      {s.application && (
        <Bloc id="tdb-application" Icon={Smartphone} ton="application" titre="Application des familles"
          aide="Qui a créé son compte, confirmé son adresse, utilisé ou installé l'application."
          testid="section-application">
          <div data-testid="dashboard-application">
            <CartesApplication resume={s.application} />
          </div>
        </Bloc>
      )}

      {/* Les visites du site et, depuis août, formulaires et Google (école, 23.10.2026). */}
      <SectionSite />

      <Bloc id="tdb-analyses" Icon={PieChart} ton="analyses" titre="Vue d'ensemble" aide="Les mêmes chiffres, lus d'un coup d'œil." testid="section-graphiques">
        <Graphiques s={s} />
      </Bloc>

      <Bloc Icon={Table2} ton="analyses" titre="Les élèves par jour" aide="Et ce qui reste comme places." testid="section-par-jour">
        <Tableau
          testid="tableau-par-jour"
          entetes={["Jour", { label: "Cours", droite: true }, { label: "Élèves", droite: true }, { label: "Places libres", droite: true }, "Remplissage"]}
          lignes={s.par_jour.map((l) => ({ ...l, id: l.cle }))}
          rendu={(l) => (
            <>
              <Cellule fort>{l.cle}</Cellule>
              <Cellule droite muet>{l.cours}</Cellule>
              <Cellule droite fort>{l.eleves}</Cellule>
              <Cellule droite>{l.libres}</Cellule>
              <Cellule><Barre pourcent={l.remplissage} /></Cellule>
            </>
          )}
        />
      </Bloc>

      <Bloc Icon={BarChart3} ton="analyses" titre="Les élèves par discipline" testid="section-par-discipline">
        <Tableau
          testid="tableau-par-discipline"
          entetes={["Discipline", { label: "Cours", droite: true }, { label: "Élèves", droite: true }, { label: "Places libres", droite: true }, "Remplissage"]}
          lignes={s.par_discipline.map((l) => ({ ...l, id: l.cle }))}
          rendu={(l) => (
            <>
              <Cellule fort>{l.cle}</Cellule>
              <Cellule droite muet>{l.cours}</Cellule>
              <Cellule droite fort>{l.eleves}</Cellule>
              <Cellule droite>{l.libres}</Cellule>
              <Cellule><Barre pourcent={l.remplissage} /></Cellule>
            </>
          )}
        />
      </Bloc>

      <ParCours cours={s.par_cours} onOuvrir={(c) => onOuvrirCours(c.id)} />
    </div>
  );
}

/* Les spectacles (Jen, 01.11.2026 : « dans la section stage - audition - spectacle, ajoute les
   stats des spectacles de Noël, juin, etc. »). Noël : la salle réservable du site (places
   prises sur la jauge, liste d'attente). Juin : la billetterie (billets vendus, recettes,
   bénévoles, entrés le soir même), seulement là où son grand chantier est ouvert ; une ligne
   par représentation. Chaque carte ouvre l'écran du spectacle. */
function ChiffresSpectacles({ s, onOuvrir }) {
  const chantiers = useChantiers("console");
  const [salles, setSalles] = useState(null);
  const [juin, setJuin] = useState(null);
  useEffect(() => { api("/spectacles/salles").then((r) => setSalles(r.salles || [])).catch(() => setSalles([])); }, []);
  useEffect(() => {
    if (chantiers.spectacles_juin) api("/billetterie").then(setJuin).catch(() => setJuin(null));
  }, [chantiers.spectacles_juin]);
  const jauge = s.places_salle_spectacle || 250;
  // Les réservations de Noël par spectacle (/stats), complétées de la salle (jauge, attente).
  const noels = (s.spectacles_par_titre || []).map((x) => {
    const salle = (salles || []).find((y) => y.spectacle === x.titre);
    return { ...x, capacite: salle?.capacite || jauge, occupees: salle ? salle.occupees : x.personnes,
             attente: salle ? salle.attente.length : 0, attente_places: salle ? salle.attente.reduce((n, l) => n + (l.places || 0), 0) : 0 };
  });
  // Noël : son écran du menu Spectacles là où il existe, sinon sa feuille dans Liste.
  const versNoel = (titre) => (chantiers.spectacles_juin ? () => onOuvrir?.("spectacles-noel")
    : () => window.dispatchEvent(new CustomEvent("console-aller", { detail: { onglet: "liste", id: `spectacles::${titre}` } })));
  const t = juin?.totaux;
  const reps = juin?.representations || [];
  const recettes = (x) => Object.values(x.recettes || {}).reduce((n, v) => n + Number(v || 0), 0);
  const manque = (x) => (x.ouverte ? x.roles.reduce((n, r) => n + r.restantes, 0) : 0);
  const entres = reps.reduce((n, x) => n + (x.entres || 0), 0);
  return (
    <div className="space-y-3" data-testid="stat-spectacles-bloc">
      <div className="text-[11px] font-semibold uppercase tracking-[0.16em]" style={{ color: TONS_BLOC.annexes.texte }}>Spectacles</div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {noels.length ? noels.map((x) => (
          <Carte key={x.titre} titre={x.titre} valeur={`${x.occupees} / ${x.capacite}`}
            detail={`places réservées${x.quand ? ` · ${x.quand}` : ""} · ${x.reservations} réservation${x.reservations > 1 ? "s" : ""}${x.attente ? ` · ${x.attente} en liste d'attente (${x.attente_places} place${x.attente_places > 1 ? "s" : ""})` : ""}`}
            ton={x.occupees >= x.capacite ? "rouge" : "neutre"} onClick={versNoel(x.titre)} testid="stat-spectacles" />
        )) : (
          <Carte titre="Spectacle de Noël" valeur={`0 / ${jauge}`} detail="aucune réservation pour l'instant" testid="stat-spectacles" />
        )}
        {t && (
          <>
            <Carte titre="Billets de juin vendus" valeur={t.billets} ton="primaire"
              detail={`${t.commandes} commande${t.commandes > 1 ? "s" : ""} · ${t.personnes} personnes (0-2 ans compris)`}
              onClick={() => onOuvrir?.("spectacles-commandes")} testid="stat-juin-billets" />
            <Carte titre="Recettes des billets" valeur={argent(t.recettes || 0)} ton="vert"
              detail="billets payés, sans les frais de paiement" onClick={() => onOuvrir?.("spectacles")} testid="stat-juin-recettes" />
            <Carte titre="Bénévoles de juin" valeur={t.benevoles} ton={t.manquent ? "ambre" : "vert"}
              detail={`${t.creneaux} créneau${t.creneaux > 1 ? "x" : ""} pris · ${t.manquent ? `il en manque ${t.manquent}` : "tous les postes sont pris"}`}
              onClick={() => onOuvrir?.("spectacles-benevoles")} testid="stat-juin-benevoles" />
          </>
        )}
      </div>
      {reps.length > 0 && (
        <div className="rounded-2xl border bg-white overflow-x-auto" style={{ borderColor: "var(--border)" }}>
          <table className="w-full text-sm" data-testid="stat-juin-representations">
            <thead><tr className="text-left text-[10px] uppercase tracking-[0.12em] text-[var(--muted)] border-b" style={{ borderColor: "var(--border)" }}>
              <th className="py-2 px-4 font-semibold">Spectacles de juin</th>
              <th className="py-2 px-3 font-semibold">Places vendues</th>
              <th className="py-2 px-3 font-semibold text-right">Recettes</th>
              <th className="py-2 px-3 font-semibold text-right">Bénévoles manquants</th>
              {entres > 0 && <th className="py-2 px-4 font-semibold text-right">Entrés</th>}
            </tr></thead>
            <tbody>
              {reps.map((x) => (
                <tr key={x.id} className="border-b last:border-b-0" style={{ borderColor: "var(--border-soft)" }}>
                  <td className="py-2 px-4"><span className="font-medium">{x.titre}</span> <span className="text-[var(--muted)]">· {x.quand}</span>
                    {!x.ouverte && <span className="ml-2 text-[11px] text-[var(--muted)]">(fermée)</span>}</td>
                  <td className="py-2 px-3 min-w-[11rem]">
                    <div className="flex items-center gap-3">
                      <span className="w-24 h-1.5 shrink-0 rounded-full overflow-hidden" style={{ background: "#EDE9FE" }}>
                        <span className="block h-full rounded-full" style={{ width: `${x.places ? Math.min(100, (100 * x.vendues) / x.places) : 0}%`, background: TONS_BLOC.annexes.accent }} />
                      </span>
                      <span className="tabular-nums whitespace-nowrap"><strong>{x.vendues}</strong> / {x.places}</span>
                    </div>
                  </td>
                  <td className="py-2 px-3 text-right tabular-nums">{argent(recettes(x))}</td>
                  <td className="py-2 px-3 text-right tabular-nums" style={{ color: manque(x) ? "#B45309" : "#15803D" }}>{x.ouverte ? manque(x) || "✓" : "—"}</td>
                  {entres > 0 && <td className="py-2 px-4 text-right tabular-nums">{x.entres} / {x.attendus}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const JOURS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];

/* Les cours à surveiller, groupés par jour : on lit une journée d'un coup d'œil
   et un clic mène à la feuille du cours. */
const ListeParJour = ({ titre, items, Icon, ton, aide, onOuvrir, testid }) => {
  const couleurs = ton === "ambre"
    ? { pastille: "#FDE68A", icone: "#B45309", pilule: "#FFFBEB", bord: "#FDE68A", texte: "#B45309" }
    : { pastille: "#BBF7D0", icone: "#15803D", pilule: "#F0FDF4", bord: "#BBF7D0", texte: "#15803D" };
  const jours = JOURS.map((j) => [j, items.filter((c) => (c.jour || "").toLowerCase() === j.toLowerCase())])
    .filter(([, l]) => l.length > 0);

  return (
    <div className="bg-white rounded-2xl border overflow-hidden flex flex-col" style={{ borderColor: "var(--border)" }} data-testid={testid}>
      <div className="px-5 py-4 flex items-center gap-3 border-b" style={{ borderColor: "var(--border-soft)" }}>
        <span className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
          style={{ background: couleurs.pastille, color: couleurs.icone }}>
          <Icon size={17} />
        </span>
        <div className="min-w-0">
          <div className="font-display text-base leading-tight">{titre}</div>
          {aide && <div className="text-[11px] text-[var(--muted)] mt-0.5">{aide}</div>}
        </div>
        <span className="ml-auto text-sm font-semibold px-2.5 py-1 rounded-full"
          style={{ background: couleurs.pilule, color: couleurs.texte, border: `1px solid ${couleurs.bord}` }}>
          {items.length}
        </span>
      </div>

      {items.length === 0 ? (
        <p className="px-5 py-8 text-sm text-[var(--muted)]">Rien à signaler.</p>
      ) : (
        <div className="max-h-[22rem] overflow-auto">
          {jours.map(([jour, liste]) => (
            <div key={jour}>
              <div className="px-5 py-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] sticky top-0"
                style={{ background: "#F1F5F9", color: "#475569" }}>
                {jour} <span style={{ opacity: 0.6 }}>· {liste.length}</span>
              </div>
              {liste.map((c) => (
                <button
                  key={c.id}
                  onClick={() => onOuvrir?.(c.id)}
                  data-testid={`${testid}-ligne`}
                  className="w-full text-left px-5 py-2.5 flex items-center gap-3 border-b transition-colors"
                  style={{ borderColor: "var(--border-soft)" }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "var(--primary-tint)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                >
                  <span className="text-xs tabular-nums font-semibold w-11 shrink-0" style={{ color: "var(--muted)" }}>{c.debut}</span>
                  <span className="text-sm font-medium truncate">{c.nom}</span>
                  <span className="ml-auto text-[11px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap"
                    style={{ background: couleurs.pilule, color: couleurs.texte, border: `1px solid ${couleurs.bord}` }}>
                    {c.inscrits === undefined ? "complet" : `${c.inscrits} élève${c.inscrits === 1 ? "" : "s"}`}
                  </span>
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Liste = ({ titre, items, rendu, icone, testid }) => (
  <div className="bg-white rounded-2xl p-5 border" style={{ borderColor: "var(--border)" }} data-testid={testid}>
    <div className="flex items-center gap-2 mb-3">
      {icone}
      <div className="font-display text-base">{titre}</div>
      <span className="ml-auto text-xs font-semibold text-[var(--muted)]">{items.length}</span>
    </div>
    {items.length === 0 ? (
      <p className="text-sm text-[var(--muted)]">Rien à signaler.</p>
    ) : (
      <ul className="space-y-1.5 text-sm max-h-60 overflow-auto pr-1">
        {items.map((i, k) => <li key={k} className="flex gap-2"><span style={{ color: "var(--primary)" }}>·</span>{rendu(i)}</li>)}
      </ul>
    )}
  </div>
);
